import { z } from 'zod';

/**
 * Client staff portal (alerts center, D-129): the shared contract of the portal API (`/api/p/*`) and the admin
 * access API (`/api/portal-access`). Used by the portal process, the app and both web bundles.
 */

/** An access link slug: 12 random base62 chars (not the client slug). */
export const PORTAL_SLUG_RE = /^[A-Za-z0-9]{12}$/;
/**
 * Request header with the slug of the link the browser shows, sent on GET /api/p/session only (never in a URL query: the
 * slug is half a credential). The answer's `sameAccess` tells whether the session belongs to that link. Never logged.
 */
export const PORTAL_SLUG_HEADER = 'x-nw-slug';
export const PORTAL_NAME_MIN = 2;
export const PORTAL_NAME_MAX = 40;
export const PORTAL_MAX_ACCESSES_PER_CLIENT = 10;

export const PortalLoginSchema = z.object({
  slug: z.string().regex(PORTAL_SLUG_RE),
  password: z.string().min(1).max(200),
});
/** Cleaned server-side (cleanDisplayName). */
export const PortalNameSchema = z.object({ name: z.string().max(200) });
export const PortalPasswordChangeSchema = z.object({
  current: z.string().min(1).max(200),
  next: z.string().min(10).max(200),
});
export const PortalStepUpSchema = z.object({ totpCode: z.string().trim().min(6).max(20) });
export const PortalAccessCreateSchema = z.object({
  clientId: z.string().min(1),
  label: z.string().trim().min(2).max(60),
  canHandle: z.boolean().default(true),
  canManage: z.boolean().default(false),
  /** may reveal a customer's full phone on a case (W3-2 "הצג מספר", audited) - R3 */
  showPhones: z.boolean().default(true),
  totpCode: z.string().trim().min(6).max(20),
});
/** Change the capabilities of an existing access (W3-3: canManage is turned on per access later). */
export const PortalAccessUpdateSchema = z
  .object({
    canHandle: z.boolean().optional(),
    canManage: z.boolean().optional(),
    showPhones: z.boolean().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'בחרו הרשאה לשינוי' });

export type PortalLogin = z.infer<typeof PortalLoginSchema>;
export type PortalName = z.infer<typeof PortalNameSchema>;
export type PortalPasswordChange = z.infer<typeof PortalPasswordChangeSchema>;
export type PortalAccessCreate = z.infer<typeof PortalAccessCreateSchema>;
export type PortalAccessUpdate = z.infer<typeof PortalAccessUpdateSchema>;
export type PortalStepUp = z.infer<typeof PortalStepUpSchema>;

export interface PortalClientView {
  name: string;
  kind: 'client' | 'parent';
  language: 'he' | 'en';
}

export type PortalSessionView =
  | { authenticated: false }
  | {
      authenticated: true;
      client: PortalClientView;
      accessLabel: string;
      displayName: string | null;
      canHandle: boolean;
      canManage: boolean;
      showPhones: boolean;
      /** the clients this access sees, without a parent itself, by name ([itself] for a client) - R2 */
      branches: { id: string; name: string }[];
      expiresAt: string;
      /**
       * GET /api/p/session with the PORTAL_SLUG_HEADER only: false when the session belongs to another access than the link
       * in the browser (the web shows that link's login). Absent without the header (login, name) - read as true.
       */
      sameAccess?: boolean;
    };

export interface PortalAccessView {
  id: string;
  clientId: string;
  label: string;
  /** null without clients.portal (R28); a secret / create response always has it */
  link: string | null;
  canHandle: boolean;
  canManage: boolean;
  showPhones: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  revokedAt: string | null;
  activeSessions: number;
}

/** Returned once by create / reset; the password is never stored, logged or audited. */
export interface PortalAccessSecret {
  access: PortalAccessView;
  password: string;
}

export interface PortalLogRow {
  at: string;
  action: string;
  label: string;
  actorName: string;
  via: 'portal' | 'ui' | 'system';
}

export const PORTAL_ACTION_LABELS_HE: Record<string, string> = {
  'portal_access.created': 'נוצרה גישה',
  'portal_access.password_reset': 'NIVISION יצרו סיסמה חדשה',
  'portal_access.revoked': 'הגישה בוטלה',
  'portal.login.ok': 'כניסה',
  'portal.login.fail': 'ניסיון כניסה עם סיסמה שגויה',
  'portal.lockout': 'הגישה ננעלה זמנית אחרי 10 ניסיונות שגויים',
  'portal.name.set': 'הוקלד שם',
  'portal.password.changed': 'הלקוח החליף סיסמה',
  'portal.logout': 'יציאה',
  'portal_access.updated': 'NIVISION שינו את הרשאות הגישה',
  'portal.case.phone_shown': 'הוצג מספר טלפון של לקוח',
  'portal.cases.export': 'ייצוא התראות לאקסל',
};

/** The Hebrew label of an audit action, else the action key itself. */
export function portalActionLabel(action: string): string {
  return Object.prototype.hasOwnProperty.call(PORTAL_ACTION_LABELS_HE, action)
    ? PORTAL_ACTION_LABELS_HE[action]!
    : action;
}

export const PORTAL_MESSAGES_HE = {
  loginFailed: 'הסיסמה שגויה או שהגישה נעולה זמנית. נסו שוב מאוחר יותר.',
  rateLimited: 'יותר מדי ניסיונות מהרשת הזו. נסו שוב בעוד כמה דקות.',
  csrf: 'הבקשה נחסמה. רעננו את הדף ונסו שוב.',
  unauthenticated: 'החיבור הסתיים. נכנסים שוב עם הסיסמה.',
  nameRequired: 'צריך להקליד שם לפני שממשיכים.',
  badName: 'שם בין 2 ל-40 תווים, עם אותיות.',
  forbidden: 'לגישה הזו אין הרשאה לפעולה הזו.',
  wrongCurrent: 'הסיסמה הנוכחית שגויה.',
  weakNext: 'הסיסמה החדשה צריכה 10 תווים לפחות, שונה מהנוכחית ומהקישור.',
  forgot: 'שכחתם את הסיסמה? פנו לנציג NIVISION שלכם.',
} as const;
